import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Alert,
} from "@mui/material";
import UserDialog from "./components/UserDialog";
import StudyDropDown from "../../common/components/StudyDropDown";

import StudentEditDialog from "./components/StudentEditDialog";
import { useDeleteStudentQuery } from "../../api/useDeleteStudent";

import { useGetStudentsQuery } from "../../api/useGetStudent";
import { STATUS } from "../../common/constants/status";
import StatusDropDown from "./../../common/components/StatusDropDown";
import StatusChip from "../../common/components/StatusChip";
import { formatDate } from "../../utils/date";
import { useDeleteAllStudentsQuery } from "../../api/useDeleteAllStudents";

const AdminStudentPage = () => {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [apiSearchTerm, setApiSearchTerm] = React.useState("");
  const [selectedStudy, setSelectedStudy] = React.useState("");
  const [selectedStatus, setSelectedStatus] = React.useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(true);

  const [errorMessage, setErrorMessage] = useState("");
  const [editData, setEditData] = useState(null);
  const { mutate: deleteStudent } = useDeleteStudentQuery();
  const {
    mutate: deleteAllStudents,
    isPending: isDeleteAllStudentsLoading,
    error: deleteAllStudentsError,
  } = useDeleteAllStudentsQuery();
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  const openEditModal = () => setIsEditModalOpen(true);
  const closeEditModal = () => {
    setEditData(null);
    setIsEditModalOpen(false);
  };

  const {
    data: students,
    isLoading,
    isError,
    error,
  } = useGetStudentsQuery({
    studyId: selectedStudy,
    status: selectedStatus,
    keyword: apiSearchTerm,
  });
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };
  const search = (event) => {
    setApiSearchTerm(searchTerm);
  };
  const handleEditClick = (user) => {
    setEditData(user);
    openEditModal();
  };

  const handleStudyChange = (event) => {
    setSelectedStudy(event.target.value);
  };

  const handleStatusChange = (event) => {
    setSelectedStatus(event.target.value);
  };

  const handleNewUser = () => {
    setEditData(null);
    openModal();
  };
  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      search();
    }
  };
  const handleDeleteClick = (userId) => {
    if (window.confirm("삭제하시겠습니까?")) {
      deleteStudent(userId);
    }
  };

  const handleDeleteAllStudents = () => {
    if (window.confirm("전체 학생을 삭제하시겠습니까?")) {
      deleteAllStudents({ studyId: selectedStudy._id });
    }
  };

  if (isLoading) {
    return <div>loading...</div>;
  }
  if (isError) {
    setErrorMessage(error.response.data.message);
  }
  return (
    <Box sx={{ width: "100%", padding: 2 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={4}>
          <StudyDropDown
            selectedStudy={selectedStudy}
            handleStudyChange={handleStudyChange}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatusDropDown
            selectedStatus={selectedStatus}
            handleStatusChange={handleStatusChange}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <TextField
            fullWidth
            label="이름 또는 이메일 검색"
            variant="outlined"
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleKeyPress}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button onClick={search}>검색</Button>
        </Grid>

        <Grid item sm={2} xs={12}>
          <Button variant="contained" fullWidth onClick={handleNewUser}>
            학생 추가
          </Button>
        </Grid>
        <Grid item sm={2} xs={12}>
          <Button
            variant="contained"
            color="error"
            fullWidth
            onClick={handleDeleteAllStudents}
            disabled={isDeleteAllStudentsLoading}
          >
            {isDeleteAllStudentsLoading ? "loading.." : "전체 삭제"}
          </Button>
        </Grid>
      </Grid>
      {deleteAllStudentsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {deleteAllStudentsError.response.data.error}
        </Alert>
      )}
      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {!students || students.length === 0 ? (
        <div>학생이 없음</div>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              총 학생수:{students.length} / active:{" "}
              {students.filter((std) => std.status === STATUS.active).length}
            </div>
            <Button
              variant="contained"
              onClick={() =>
                setShowDetails((prevShowDetails) => !prevShowDetails)
              }
            >
              {showDetails ? "컬럼 숨기기" : "컬럼 보이기"}
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ marginTop: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>이름</TableCell>
                  <TableCell>이메일</TableCell>
                  <TableCell>지역</TableCell>
                  {showDetails && <TableCell>상태</TableCell>}
                  {showDetails && <TableCell>미제출 과제</TableCell>}
                  {showDetails && <TableCell>fail 과제</TableCell>}
                  {showDetails && <TableCell>메모</TableCell>}
                  {showDetails && <TableCell>가입여부</TableCell>}
                  {showDetails && <TableCell>수정/삭제</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {students.map((student) => (
                  <TableRow key={student.userInfo.email}>
                    <TableCell>{student.userInfo.name}</TableCell>
                    <TableCell>{student.userInfo.email}</TableCell>
                    <TableCell>{student.userInfo.region}</TableCell>
                    {showDetails && (
                      <TableCell>
                        <StatusChip status={student.status} />
                      </TableCell>
                    )}
                    {showDetails && (
                      <TableCell>
                        <ul>
                          {student.missedAssignments?.map((item) => {
                            return <li>{formatDate(item.closeDate)}</li>;
                          })}
                        </ul>
                      </TableCell>
                    )}
                    {showDetails && (
                      <TableCell>
                        <ul>
                          {student.failAssignments?.map((item) => {
                            return <li>{formatDate(item.closeDate)}</li>;
                          })}
                        </ul>
                      </TableCell>
                    )}
                    {showDetails && <TableCell>{student.note}</TableCell>}
                    {showDetails && (
                      <TableCell>
                        {student.userInfo.isSignUp ? "가입완료" : "가입안함"}
                      </TableCell>
                    )}

                    {showDetails && (
                      <TableCell>
                        <Button
                          size="small"
                          onClick={() => handleEditClick(student)}
                        >
                          수정
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(student._id)}
                        >
                          삭제
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* {!students || students.length === 0 ? (
        <div>학생이 없습니다.</div>
      ) : (
       
      )} */}
      <UserDialog isModalOpen={isModalOpen} closeModal={closeModal} />
      <StudentEditDialog
        isModalOpen={isEditModalOpen}
        closeModal={closeEditModal}
        data={editData}
      />
    </Box>
  );
};

export default AdminStudentPage;
